import { mayRestoreSettings, settingsPartWriters, settingsRestorePolicy, settingsSectionKey } from '@norvix/utils';

import { useAuth } from '../../auth/useAuth.js';

import { canReadSection, canWriteSection, type SettingsSection } from './settings-sections.js';

/**
 * May the signed-in staff member change this settings section (DEV-140)? A
 * section they may only read shows no save bar and no edit controls; the API
 * refuses the save with 403 either way.
 */
export function useCanWriteSection(section: Pick<SettingsSection, 'group' | 'id'>): boolean {
  const { user } = useAuth();
  return canWriteSection(user?.role, section);
}

/** May the signed-in staff member open this settings section? */
export function useCanReadSection(section: Pick<SettingsSection, 'group' | 'id'>): boolean {
  const { user } = useAuth();
  return canReadSection(user?.role, section);
}

export interface SectionRestore {
  /** "Restore this version" is offered to this person: the section offers it and they may change it. */
  readonly canRestore: boolean;
  /** A restore takes effect only once a second writer approves it (DEV-142). */
  readonly fourEyes: boolean;
  /** What the History says about restoring, if anything. */
  readonly note?: string;
}

/** How restore works on this section for the signed-in staff member (DEV-142), from the shared registry. */
export function useSectionRestore(section: Pick<SettingsSection, 'group' | 'id'>): SectionRestore {
  const { user } = useAuth();
  const key = settingsSectionKey(section);
  const policy = settingsRestorePolicy(key);
  if (!policy.offered) return { canRestore: false, fourEyes: false, note: policy.why };
  const fourEyes = policy.approval === 'second-approver';
  return {
    canRestore: mayRestoreSettings(user?.role, key),
    fourEyes,
    ...(fourEyes ? { note: 'A restore here changes who gets flagged, so it takes effect only after a second person who may change this section approves it.' } : {}),
  };
}

/** May they change one part of a section whose changes are narrower (e.g. provider costs)? */
export function useCanWriteSectionPart(section: Pick<SettingsSection, 'group' | 'id'>, partId: string): boolean {
  const { user } = useAuth();
  const role = user?.role;
  return role !== null && role !== undefined
    && (settingsPartWriters(settingsSectionKey(section), partId) as readonly string[]).includes(role.toUpperCase());
}
